import { differenceInSeconds, parseISO } from 'date-fns'
import { useEffect, useState } from 'react'

const TICK_INTERVAL_IN_MS = 1000

/**
 * Seconds left until `expiresAt`, clamped at 0 so an already-expired session
 * never counts into the negative.
 */
const getSecondsRemaining = (expiresAt: string): number =>
  Math.max(0, differenceInSeconds(parseISO(expiresAt), new Date()))

/**
 * Seconds remaining until `expiresAt`, clamped at `0` and re-evaluated every
 * second. Drives the temporary-session countdowns in the banner and the
 * End-session dialog.
 *
 * Deliberately uses a plain `setInterval` rather than `useInterval` from
 * `@backoffice-frontend/hooks`: that hook delegates to `usePolling`, which
 * no-ops when polling is disabled (E2E and PR builds), which would silently
 * freeze the countdown.
 *
 * The value is recomputed from `expiresAt` on every tick instead of being
 * decremented, so a throttled/backgrounded tab does not drift.
 */
export const useCountdown = (expiresAt: string): number => {
  const [secondsRemaining, setSecondsRemaining] = useState(() =>
    getSecondsRemaining(expiresAt),
  )

  useEffect(() => {
    setSecondsRemaining(getSecondsRemaining(expiresAt))

    const intervalId = setInterval(() => {
      const remaining = getSecondsRemaining(expiresAt)
      setSecondsRemaining(remaining)

      if (remaining <= 0) {
        clearInterval(intervalId)
      }
    }, TICK_INTERVAL_IN_MS)

    return () => clearInterval(intervalId)
  }, [expiresAt])

  return secondsRemaining
}
